/**
 * The contents of this file are mostly copied over from conversation form
 * reducer file. The logic can be improved but since this is a legacy code it's
 * not worth spending time on it.
 */
import {
  Conversation,
  ConversationFormEmailUser,
  ConversationFormValues,
  EmailIntegration,
  Message,
  MessageEmailRecipient,
} from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import uniqBy from 'lodash/uniqBy';

export function getConversationFormName(csid: string | number): string {
  return `conversationForm_${csid}`;
}

export function getConversationFormEmailInitialValues(
  conversation?: Conversation,
  fromEmails?: EmailIntegration[],
): ConversationFormValues {
  if (!conversation?.events?.length || !fromEmails?.length) {
    return {};
  }

  const requester = conversation.requested_by
    ? conversation.authors?.[conversation.requested_by]
    : null;
  const messageEvents =
    conversation.events?.filter(
      (event: Message) =>
        // TODO [CONV-258]:remove the `contactform` exception once the backend fixes the issue with from and to emails.
        (event.channel === 'contactform' || !event.is_auto_reply) &&
        ['conversation:message_added', 'conversation:add_message'].includes(
          event.type,
        ),
    ) ?? [];

  const lastMessage =
    messageEvents.reverse().find((message: Message) => message.from) ?? null;

  // We try to get the from address from the last message.
  const fromEmail =
    findEmailIntegration(
      fromEmails,
      conversation.attributes?.integration_email,
    ) || null;

  let to: ConversationFormEmailUser[] | null = null;
  let cc: ConversationFormEmailUser[] | null = null;
  let bcc: ConversationFormEmailUser[] | null = null;

  // We look at the last message in the message event list with a from attribute on.
  if (lastMessage) {
    let toArray =
      lastMessage.reply_default_to_emails !== undefined
        ? JSON.parse(lastMessage.reply_default_to_emails)
        : null;

    // if there is no cc on the reply_default_to_emails then we look at the to_list_v2.
    if (toArray === null || toArray.length === 0) {
      toArray =
        lastMessage.to_list_v2 !== undefined
          ? JSON.parse(lastMessage.to_list_v2)
          : null;
    }

    if (toArray) to = toArray.map(toUser);

    const ccArray =
      lastMessage.reply_default_cc_emails !== undefined
        ? JSON.parse(lastMessage.reply_default_cc_emails)
        : null;

    if (ccArray) {
      cc = ccArray.map(toUser);
    }

    let bccArray =
      lastMessage.reply_default_bcc_emails !== undefined
        ? JSON.parse(lastMessage.reply_default_bcc_emails)
        : null;
    if (bccArray === null || bccArray.length === 0) {
      bccArray =
        lastMessage.bcc_list_v2 !== undefined
          ? JSON.parse(lastMessage.bcc_list_v2)
          : null;
    }
    if (bccArray) {
      bcc = bccArray.map(toUser);
    }
  }

  if (conversation.initial_channel === 'contactform') {
    // If the To matches an integration email (or it's SPF alias), it's possible that we have selected the wrong To email.
    // This is due to a long-standing issue with the contact form where the From and To were reversed.
    to =
      to?.filter(
        ({ user }) =>
          !fromEmails?.some(
            ({ email_address, spf_sender_email }) =>
              user?.email === email_address || user?.email === spf_sender_email,
          ),
      ) ?? null;
  }

  // We need a fallback to conversation attributes (because of old conversations and contact forms).
  if (!to?.length) {
    const toArray: MessageEmailRecipient[] = [];

    if (requester?.email) {
      toArray.push({ email: requester.email, name: requester?.name ?? '' });
    } else if (
      conversation.attributes &&
      (conversation.attributes.to_email || conversation.attributes.from_email)
    ) {
      const email =
        conversation.direction === 'inbound'
          ? conversation.attributes.from_email
          : conversation.attributes.to_email;
      const name =
        requester?.email === email && requester?.name ? requester.name : '';

      toArray.push({ email: email, name: name });
    }

    if (toArray.length > 0) to = toArray.map(toUser);
  }

  return {
    fromEmail,
    to,
    cc,
    bcc,
  };
}

function toUser(email: MessageEmailRecipient): ConversationFormEmailUser {
  return { user: email };
}

function findEmailIntegration(
  fromEmails: EmailIntegration[],
  fromAddress?: string,
): string | null | undefined {
  if (!fromAddress) return null;

  // We check that the from address is one of our email integrations.
  for (const fromEmail of fromEmails) {
    const { email_address, spf_sender_email } = fromEmail;
    if ([email_address, spf_sender_email].includes(fromAddress)) {
      return fromEmail.email_address;
    }
  }
}

export function getUniqueRecipientEmails(
  values?: ConversationFormEmailUser<MessageEmailRecipient | User>[],
): MessageEmailRecipient[] | undefined {
  if (!values?.length) {
    return;
  }

  const emails = values.map(({ user }) => ({
    email: user.email || '',
    name: user.name?.trim(),
  }));

  return uniqBy(emails, ({ email }) => email);
}
